/**
 * The server's refusal of an attribute name, read off the wire error.
 *
 * This is NOT a second copy of the client-side check in
 * `attributeNameConflicts` — that one covers every clash the browser can see,
 * and this one covers only the clashes it cannot:
 *
 * - `spaces` may be ACL-filtered, so the browser cannot see every space-local a
 *   global would collide with.
 * - The promotion pre-check (`attributePromotionPreflight`) deliberately fails
 *   OPEN — a transport error reports "no conflicts" rather than blocking.
 * - The catalog is read at mount and not re-read on submit, so another steward
 *   can claim the name in between.
 *
 * In all three the client check says "free" and the server still refuses
 * (DV-510 / DV-511).
 *
 * Matched on the error message rather than `extensions.code`: the codes are
 * prefixed onto the message server-side precisely so a client reading only
 * `message` can branch, and it keeps this module free of Apollo types. Same
 * approach as `SpaceEditor/errors.ts`.
 */
import { CHOOSE_A_DIFFERENT_NAME } from "#common/attributeNameConflicts";

/**
 * Both refusals a name can earn: an active attribute already displays this
 * label, or already holds the slug this label mints.
 *
 * They share one message. The slug half is belt-and-braces rather than a live
 * gap — create mints a random suffix onto every new slug (DV-517) and promote
 * keeps its existing unique one, so a slug clash is close to unreachable — and
 * when it does surface, "pick another name" is the same fix, because the slug is
 * derived from the name. Reporting it as a slug mechanism would be jargon for a
 * failure the steward resolves identically.
 */
const NAME_CONFLICT_RE = /ATTRIBUTE_LABEL_CONFLICT|ATTRIBUTE_SLUG_CONFLICT/;

/**
 * Whether the server refused the NAME, as opposed to failing for some other
 * reason. Deliberately narrow: the container keys its toast off this, so a false
 * positive would swallow an unrelated failure that nothing else reports.
 */
export const isAttributeNameConflictError = (error: unknown): boolean =>
  error instanceof Error && NAME_CONFLICT_RE.test(error.message);

/**
 * The inline message for a refused name, or `null` when `error` is something
 * else — a caller returning `null` should fall back to its generic reporting
 * (the container's toast) rather than swallowing the failure.
 *
 * Deliberately does NOT say where the clash lives, which is the one thing this
 * message cannot borrow from `attributeNameConflictMessage`. The server does not
 * report the holder, and it may be a local in a space this steward cannot read,
 * so naming "a global attribute" or a space would be a guess.
 */
export function attributeNameConflictErrorMessage(name: string, error: unknown): string | null {
  if (!isAttributeNameConflictError(error)) return null;
  return `“${name}” is already used by another attribute. ${CHOOSE_A_DIFFERENT_NAME}`;
}
